// src/hooks/useChat.js
// ─────────────────────────────────────────────────────────────
// Claude API integration for the AI chat sidebar (PL-06)
// and natural language search (DS-07).
//
// Calls the Anthropic API directly from the browser.
// NOTE: In production, this moves to a Firebase Cloud Function
// so the API key is not exposed. For local dev, the key lives
// in .env.local as REACT_APP_ANTHROPIC_API_KEY.
// ─────────────────────────────────────────────────────────────

import { useState, useCallback, useRef } from 'react';
import { track, EVENTS } from '../lib/firebase';

// System prompt — gives Claude the context it needs to be
// a Signal Post resource navigator, not a general assistant.
const SYSTEM_PROMPT = `You are the Signal Post AI assistant — a resource navigator for Canadian Veterans, CAF members, RCMP members, and their families.

Signal Post is an independent, non-profit directory of 118+ verified programs across Canada covering mental health, financial support, housing, employment, family services, and medical rehabilitation.

Your job is to help users find the right programs for their situation. You are warm, plain-spoken, and direct. You never use military jargon or acronyms without explaining them.

Key facts to share when relevant:
- Many programs are accessible to family members WITHOUT the Veteran present
- Most programs listed are free or subsidized
- About 80% require no referral to access
- Programs cover all provinces and territories, plus national programs

When a user describes their situation:
1. Acknowledge what they've shared briefly and with warmth
2. Suggest 2–3 specific programs that fit their situation (use the program names and organizations from Signal Post's database)
3. Mention key practical details: cost, referral requirement, whether family can access directly
4. Invite them to use the search filters to explore further

If a user seems to be in distress, respond with care and mention that the VAC/CAF crisis line (1-800-268-7708) and Talk Suicide Canada (1-833-456-4566) are available 24/7 — but do not make this the whole response. Always also address what they asked about.

Keep responses concise — 3–5 sentences is usually right. Never use bullet points unless listing more than 3 programs. Always write in plain English.

You do NOT have access to real-time data. If asked about something outside Signal Post's scope, say so briefly and redirect to what you can help with.`;

export function useChat({ wizardAnswers = null } = {}) {
  const [messages, setMessages]   = useState([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const abortRef                  = useRef(null);

  // Build initial context message from wizard answers
  // so Claude knows the user's situation from the start
  const getContextPrefix = useCallback(() => {
    if (!wizardAnswers) return '';
    const { who, challenge, province } = wizardAnswers;
    if (!who?.length && !challenge?.length) return '';

    const whoMap = {
      veteran: 'a Veteran', caf: 'a serving CAF member', rcmp: 'an RCMP member',
      spouse: 'the spouse or partner of a Veteran', family: 'a family member of a Veteran',
      provider: 'a service provider', indigenous: 'an Indigenous Veteran or family member',
    };
    const chalMap = {
      mental: 'mental health support', financial: 'financial assistance',
      housing: 'housing support', employment: 'employment or transition support',
      family: 'family services', medical: 'medical rehabilitation',
    };

    const whoText = who.map(w => whoMap[w] || w).join(' and ');
    const chalText = challenge.map(c => chalMap[c] || c).join(', ');
    const provText = province && province !== 'all' ? ` in ${province}` : '';

    return `[Context from guided intake: The user is ${whoText}${provText}, looking for ${chalText || 'general support'}.] `;
  }, [wizardAnswers]);

  const sendMessage = useCallback(async (userText) => {
    if (!userText.trim() || loading) return;

    const userMessage = { role: 'user', content: userText.trim() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setLoading(true);
    setError(null);

    track(EVENTS.AI_MESSAGE_SENT, { message_length: userText.length });

    // Prepend context prefix to first user message
    const apiMessages = newMessages.map((m, i) => {
      if (i === 0 && m.role === 'user') {
        return { role: 'user', content: getContextPrefix() + m.content };
      }
      return m;
    });

    try {
      const controller = new AbortController();
      abortRef.current = controller;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 1000,
          system: SYSTEM_PROMPT,
          messages: apiMessages,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error?.message || `API error ${response.status}`);
      }

      const data = await response.json();
      const assistantText = data.content
        .filter(b => b.type === 'text')
        .map(b => b.text)
        .join('');

      setMessages(prev => [...prev, { role: 'assistant', content: assistantText }]);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('[useChat] API error:', err);
      setError('Something went wrong. Please try again.');
      // Remove the user message on error so they can retry
      setMessages(messages);
    } finally {
      setLoading(false);
    }
  }, [messages, loading, getContextPrefix]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);

  const cancelRequest = useCallback(() => {
    abortRef.current?.abort();
    setLoading(false);
  }, []);

  return {
    messages,
    loading,
    error,
    sendMessage,
    clearChat,
    cancelRequest,
  };
}

// ─── Natural language search (DS-07) ─────────────────────────
// Separate function from chat — takes a plain-language query
// and returns structured program match criteria.
// Used to pre-populate filters from a text description.

export async function naturalLanguageToFilters(query) {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      model: 'claude-sonnet-4-20250514',
      max_tokens: 400,
      system: `You are a filter extraction system for Signal Post, a Canadian Veteran resource directory.
      
Given a plain-language query from a user, extract structured filter criteria.
Respond ONLY with a JSON object — no preamble, no markdown, no explanation.

JSON shape:
{
  "category": "mental|financial|housing|employment|family|medical|all",
  "province": "BC|AB|SK|MB|ON|QC|NB|NS|PE|NL|YT|NT|NU|National|all",
  "familyOK": true|false,
  "noReferral": true|false,
  "freeOnly": true|false,
  "personas": ["veteran","caf","rcmp","spouse","family","provider","indigenous","rural"],
  "searchTerm": "keyword to search for, or empty string"
}

Rules:
- personas array should contain only values from the list above
- If uncertain about a field, use "all" for strings or false for booleans
- searchTerm should be a specific clinical/program term if the user mentions one, otherwise ""`,
      messages: [{ role: 'user', content: query }],
    }),
  });

  const data = await response.json();
  const text = data.content.filter(b => b.type === 'text').map(b => b.text).join('');

  try {
    return JSON.parse(text.replace(/```json|```/g, '').trim());
  } catch {
    return null;
  }
}
